import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    Paper,
    Alert,
    Button,
    Grid,
    Chip,
    List,
    ListItem,
    ListItemText,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from "@mui/material";
import { ExpandMore, BugReport, Refresh } from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";
import { 
    hasBusinessAccess, 
    hasSystemAccess, 
    getUserPermissions
} from "../utils/permissions";

const UserAccessDiagnostic = () => {
    const { currentUser, userProfile, loading, refreshUserData } = useAuth();
    const [diagnosticData, setDiagnosticData] = useState({});
    const [expanded, setExpanded] = useState("overview");

    const runDiagnostic = useCallback(() => {
        if (!currentUser || !userProfile) {
            setDiagnosticData({
                userFound: false,
                issues: ["Usuario no autenticado o perfil no cargado"]
            });
            return;
        }

        const issues = [];
        const warnings = [];
        const checks = {};

        // Verificar si es el usuario específico
        const isTargetUser = currentUser.uid === "TvxTKa8IBDg5W72ZASpkywBYfv13";
        checks.isTargetUser = isTargetUser;

        // Verificar rol
        const role = userProfile.role;
        const normalizedRole = role?.toUpperCase();
        checks.role = { current: role, normalized: normalizedRole };

        // Verificar permisos de administración
        const hasBusiness = hasBusinessAccess(userProfile);
        const hasSystem = hasSystemAccess(userProfile);
        checks.adminAccess = { business: hasBusiness, system: hasSystem };

        // Verificar permisos específicos
        const permissions = getUserPermissions(userProfile);
        checks.permissions = permissions;

        // Verificar consistencia
        const contextRole = userProfile.role;
        const profileRole = userProfile.role;
        checks.consistency = {
            contextRole,
            profileRole,
            isConsistent: contextRole === profileRole
        };

        // Identificar problemas
        if (!role) {
            issues.push("El usuario no tiene rol asignado");
        } else if (normalizedRole !== "SUPER_ADMIN" && normalizedRole !== "ADMIN") {
            issues.push(`El rol '${role}' no tiene permisos de administración`);
        }

        if (!hasBusiness && !hasSystem) {
            issues.push("El usuario no tiene acceso a administración de negocio ni sistema");
        }

        if (!checks.consistency.isConsistent) {
            warnings.push("Inconsistencia entre rol del contexto y perfil");
        }

        if (permissions.length === 0) {
            issues.push("El usuario no tiene permisos asignados");
        }

        setDiagnosticData({
            userFound: true,
            isTargetUser,
            issues,
            warnings,
            checks,
            recommendations: generateRecommendations(issues, warnings, checks)
        });
    }, [currentUser, userProfile]);

    useEffect(() => {
        runDiagnostic();
    }, [runDiagnostic]);

    const generateRecommendations = (issues, warnings, _checks) => {
        const recommendations = [];

        if (issues.includes("El usuario no tiene rol asignado")) {
            recommendations.push({
                priority: "ALTA",
                action: "Asignar rol SUPER_ADMIN en Firestore",
                command: "node scripts/fixUserRole.js aseflc@gmail.com"
            });
        }

        if (issues.includes("El rol no tiene permisos de administración")) {
            recommendations.push({
                priority: "ALTA",
                action: "Cambiar rol a SUPER_ADMIN",
                command: "Actualizar en Firestore: role = 'SUPER_ADMIN'"
            });
        }

        if (issues.includes("El usuario no tiene acceso a administración")) {
            recommendations.push({
                priority: "MEDIA",
                action: "Verificar helpers de permisos",
                command: "Revisar src/utils/permissions.js"
            });
        }

        if (warnings.includes("Inconsistencia entre rol del contexto y perfil")) {
            recommendations.push({
                priority: "MEDIA",
                action: "Refrescar datos del usuario",
                command: "Hacer logout/login o usar botón Refrescar"
            });
        }

        if (recommendations.length === 0) {
            recommendations.push({
                priority: "BAJA",
                action: "Verificar en /admin-debug",
                command: "Acceder a /admin-debug para análisis detallado"
            });
        }

        return recommendations;
    };

    const handleRefresh = async () => {
        try {
            await refreshUserData();
            runDiagnostic();
        } catch (error) {
            // console.error("Error refreshing:", error);
        }
    };

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>Cargando diagnóstico...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <BugReport sx={{ mr: 1 }} />
                    <Typography variant="h4" component="h1">
                        Diagnóstico de Acceso - Usuario Específico
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={handleRefresh}
                        sx={{ ml: "auto" }}
                    >
                        Refrescar
                    </Button>
                </Box>

                {/* Estado General */}
                <Alert 
                    severity={diagnosticData.userFound ? "info" : "error"} 
                    sx={{ mb: 3 }}
                >
                    <Typography variant="h6">
                        Estado: {diagnosticData.userFound ? "Usuario Encontrado" : "Usuario No Encontrado"}
                    </Typography>
                    {diagnosticData.userFound && (
                        <Typography variant="body2">
                            UID: {currentUser?.uid} | Email: {currentUser?.email}
                        </Typography>
                    )}
                </Alert>

                {/* Verificación de Usuario Objetivo */}
                {diagnosticData.isTargetUser && (
                    <Alert severity="success" sx={{ mb: 3 }}>
                        <Typography variant="h6">
                            ✅ Usuario Objetivo Identificado
                        </Typography>
                        <Typography variant="body2">
                            Este es el usuario TvxTKa8IBDg5W72ZASpkywBYfv13 que reporta problemas de acceso
                        </Typography>
                    </Alert>
                )}

                {/* Problemas Identificados */}
                {diagnosticData.issues && diagnosticData.issues.length > 0 && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            ❌ Problemas Identificados ({diagnosticData.issues.length})
                        </Typography>
                        <List dense>
                            {diagnosticData.issues.map((issue, index) => (
                                <ListItem key={index}>
                                    <ListItemText primary={issue} />
                                </ListItem>
                            ))}
                        </List>
                    </Alert>
                )}

                {/* Advertencias */}
                {diagnosticData.warnings && diagnosticData.warnings.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            ⚠️ Advertencias ({diagnosticData.warnings.length})
                        </Typography>
                        <List dense>
                            {diagnosticData.warnings.map((warning, index) => (
                                <ListItem key={index}>
                                    <ListItemText primary={warning} />
                                </ListItem>
                            ))}
                        </List>
                    </Alert>
                )}

                {/* Información Detallada */}
                <Accordion expanded={expanded === "overview"} onChange={handleAccordionChange("overview")}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="h6">Resumen del Diagnóstico</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Información del Usuario
                                </Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemText 
                                            primary="UID" 
                                            secondary={currentUser?.uid} 
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText 
                                            primary="Email" 
                                            secondary={currentUser?.email} 
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText 
                                            primary="Rol" 
                                            secondary={
                                                <Chip 
                                                    label={diagnosticData.checks?.role?.current || "N/A"} 
                                                    color={diagnosticData.checks?.role?.normalized === "SUPER_ADMIN" ? "error" : "default"}
                                                    size="small"
                                                />
                                            } 
                                        />
                                    </ListItem>
                                </List>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Verificaciones de Acceso
                                </Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemText 
                                            primary="Business Admin" 
                                            secondary={
                                                <Chip 
                                                    label={diagnosticData.checks?.adminAccess?.business ? "PERMITIDO" : "DENEGADO"}
                                                    color={diagnosticData.checks?.adminAccess?.business ? "success" : "error"}
                                                    size="small"
                                                />
                                            } 
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText 
                                            primary="System Admin" 
                                            secondary={
                                                <Chip 
                                                    label={diagnosticData.checks?.adminAccess?.system ? "PERMITIDO" : "DENEGADO"}
                                                    color={diagnosticData.checks?.adminAccess?.system ? "success" : "error"}
                                                    size="small"
                                                />
                                            } 
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText 
                                            primary="Permisos Totales" 
                                            secondary={diagnosticData.checks?.permissions?.length || 0} 
                                        />
                                    </ListItem>
                                </List>
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>

                {/* Recomendaciones */}
                <Accordion expanded={expanded === "recommendations"} onChange={handleAccordionChange("recommendations")}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="h6">
                            💡 Recomendaciones ({diagnosticData.recommendations?.length || 0})
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <List>
                            {diagnosticData.recommendations?.map((rec, index) => (
                                <React.Fragment key={index}>
                                    <ListItem>
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Chip 
                                                        label={rec.priority} 
                                                        color={rec.priority === "ALTA" ? "error" : rec.priority === "MEDIA" ? "warning" : "info"}
                                                        size="small"
                                                    />
                                                    <Typography variant="subtitle1">
                                                        {rec.action}
                                                    </Typography>
                                                </Box>
                                            }
                                            secondary={
                                                <Box sx={{ mt: 1 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Comando: {rec.command}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                    {index < diagnosticData.recommendations.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                    </AccordionDetails>
                </Accordion>

                {/* Datos Raw */}
                <Accordion expanded={expanded === "raw"} onChange={handleAccordionChange("raw")}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="h6">Datos Raw para Análisis</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="subtitle2" gutterBottom>
                            Datos del Usuario
                        </Typography>
                        <Paper sx={{ p: 2, bgcolor: "grey.50", fontFamily: "monospace", fontSize: "0.8rem" }}>
                            <pre>{JSON.stringify(userProfile, null, 2)}</pre>
                        </Paper>
                    </AccordionDetails>
                </Accordion>

                {/* Acciones Rápidas */}
                <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
                    <Typography variant="h6" gutterBottom>
                        Acciones Rápidas
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item>
                            <Button 
                                variant="contained" 
                                color="primary"
                                onClick={handleRefresh}
                            >
                                Refrescar Datos
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button 
                                variant="outlined"
                                onClick={() => window.open("/admin-debug", "_blank")}
                            >
                                Ir a Admin Debug
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button 
                                variant="outlined"
                                onClick={() => window.open("/auth-debug", "_blank")}
                            >
                                Ir a Auth Debug
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>
        </Box>
    );
};

export default UserAccessDiagnostic; 